import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { Box, Checkbox, Input } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { useCreateInquiryFormMutation, useUpdateInquiryFormMetadataMutation } from "../hooks";

interface InquiryFormCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pageTypeId?: string;
}

interface FormData {
  title: string;
  slug: string;
  hidden: boolean;
  emailTo: string;
  emailCc: string;
  emailBcc: string;
  emailSubject: string;
  description: string;
  successMessage: string;
  fields: string;
  contactForm: boolean;
  productInquiryForm: boolean;
  reCAPTCHA: boolean;
}

const InquiryFormCreateModal: React.FC<InquiryFormCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
  pageTypeId,
}) => {
  const [createInquiryForm, { loading: createLoading }] = useCreateInquiryFormMutation();
  const [updateMetadata, { loading: updateLoading }] = useUpdateInquiryFormMetadataMutation();

  const [formData, setFormData] = useState<FormData>({
    title: "",
    slug: "",
    hidden: false,
    emailTo: "",
    emailCc: "",
    emailBcc: "",
    emailSubject: "New Inquiry Received",
    description: "Fill this form to get in touch with us.",
    successMessage: "Thank you for reaching out. We'll respond shortly!",
    fields: "name, email, message",
    contactForm: false,
    productInquiryForm: false,
    reCAPTCHA: false,
  });

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: generateSlug(value),
    }));
  };

  const handleSubmit = async () => {
    if (!pageTypeId || !formData.title) return;

    try {
      // Step 1: Create the page
      const createResult = await createInquiryForm({
        variables: {
          input: {
            slug: formData.slug,
            title: formData.title,
            pageType: pageTypeId,
            isPublished: !formData.hidden,
          }
        }
      });

      const pageId = createResult.data?.pageCreate?.page?.id;
      
      if (pageId) {
        // Step 2: Update metadata
        await updateMetadata({
          variables: {
            id: pageId,
            input: [
              { key: "email_to", value: formData.emailTo },
              { key: "email_cc", value: formData.emailCc },
              { key: "email_bcc", value: formData.emailBcc },
              { key: "email_subject", value: formData.emailSubject },
              { key: "description", value: formData.description },
              { key: "success_message", value: formData.successMessage },
              { key: "fields", value: formData.fields },
              { key: "contact_form", value: formData.contactForm.toString() },
              { key: "product_inquiry_form", value: formData.productInquiryForm.toString() },
              { key: "reCAPTCHA", value: formData.reCAPTCHA.toString() },
            ]
          }
        });

        onSuccess();
        
        // Reset form
        setFormData({
          title: "",
          slug: "",
          hidden: false,
          emailTo: "",
          emailCc: "",
          emailBcc: "",
          emailSubject: "New Inquiry Received",
          description: "Fill this form to get in touch with us.",
          successMessage: "Thank you for reaching out. We'll respond shortly!",
          fields: "name, email, message",
          contactForm: false,
          productInquiryForm: false,
          reCAPTCHA: false,
        });
      }
    } catch (error) {
      console.error("Error creating inquiry form:", error);
    }
  };

  const loading = createLoading || updateLoading;

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          <FormattedMessage
            id="IHi/T5"
            defaultMessage="Create Inquiry Form"
          />
        </DashboardModal.Header>

        <Box display="flex" flexDirection="column" gap={4}>
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
            disabled={loading}
          />
          
          <Input
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
            required
            disabled={loading}
          />

          <Checkbox
            checked={formData.hidden}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ ...prev, hidden: !!checked }))
            }
            disabled={loading}
          >
            Hidden
          </Checkbox>

          <Input
            label="Email To"
            value={formData.emailTo}
            onChange={(e) => setFormData(prev => ({ ...prev, emailTo: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Email CC"
            value={formData.emailCc}
            onChange={(e) => setFormData(prev => ({ ...prev, emailCc: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Email BCC"
            value={formData.emailBcc}
            onChange={(e) => setFormData(prev => ({ ...prev, emailBcc: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Email Subject"
            value={formData.emailSubject}
            onChange={(e) => setFormData(prev => ({ ...prev, emailSubject: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Success Message"
            value={formData.successMessage}
            onChange={(e) => setFormData(prev => ({ ...prev, successMessage: e.target.value }))}
            disabled={loading}
          />

          <Input
            label="Fields"
            value={formData.fields}
            onChange={(e) => setFormData(prev => ({ ...prev, fields: e.target.value }))}
            disabled={loading}
            helperText="Comma-separated list of fields"
          />

          <Checkbox
            checked={formData.contactForm}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ 
                ...prev, 
                contactForm: !!checked,
                productInquiryForm: checked ? false : prev.productInquiryForm
              }))
            }
            disabled={loading}
          >
            Contact Form
          </Checkbox>

          <Checkbox
            checked={formData.productInquiryForm}
            onCheckedChange={(checked) =>
              setFormData(prev => ({
                ...prev,
                productInquiryForm: !!checked,
                contactForm: checked ? false : prev.contactForm
              }))
            }
            disabled={loading}
          >
            Product Inquiry Form
          </Checkbox>

          <Checkbox
            checked={formData.reCAPTCHA}
            onCheckedChange={(checked) =>
              setFormData(prev => ({ ...prev, reCAPTCHA: !!checked }))
            }
            disabled={loading}
          >
            reCAPTCHA
          </Checkbox>
        </Box>

        <DashboardModal.Actions>
          <BackButton onClick={onClose} />
          <ConfirmButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!formData.title || !pageTypeId || loading}
            transitionState={loading ? "loading" : "default"}
          >
            <FormattedMessage id="jvo0vs" defaultMessage="Save" />
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default InquiryFormCreateModal;